import { ethers } from "ethers";
import { normalizeHandle } from "./handle-resolver";

// These are candidates only. A local name is never proof of ownership: callers
// must decrypt its secret and match the registry before resuming publication.
export function findLocalHandleRegistrations(walletAddress: string): string[] {
  const address = walletAddress.toLowerCase();
  if (!/^0x[0-9a-f]{40}$/.test(address)) return [];
  const prefix = "stealthos:handle:hsk:";
  const suffix = `:${address}`;
  const handles = new Set<string>();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(prefix) || !key.endsWith(suffix)) continue;
    const handle = key.slice(prefix.length, -suffix.length);
    if (normalizeHandle(handle) === handle) handles.add(handle);
  }
  return [...handles].sort();
}

/** Names with a saved, encrypted attempt under `prefix` (which ends in the
 * wallet). Candidates only, like the local secrets above. */
export function findPendingHandleNames(prefix: string): string[] {
  const handles = new Set<string>();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(prefix)) continue;
    const handle = key.slice(prefix.length);
    if (normalizeHandle(handle) === handle) handles.add(handle);
  }
  return [...handles].sort();
}

export interface PendingHandleRegistration {
  idempotencyKey: string;
  body: {
    handle: string;
    action: string;
    chainScope: string;
    chain_id: number;
    handleId: string;
    recordCommitment: string;
    ownerCommitment: string;
    nullifier: string;
    epoch: number;
    serialized_transaction: string;
    user_address: string;
  };
  publication: Record<string, unknown>;
}

type RegistryState = {
  exists: boolean;
  epoch: number | bigint;
  ownerCommitment: string;
  recordCommitment: string;
};

const bytes = (value: Uint8Array): ArrayBuffer => Uint8Array.from(value).buffer;

// Save the exact payload and proof before the first relay request. Losing an
// HTTP response or reloading must not generate a different record commitment.
export async function saveHandleRegistration(
  name: string,
  key: CryptoKey,
  pending: PendingHandleRegistration,
) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: bytes(iv),
      additionalData: bytes(ethers.toUtf8Bytes(name)),
    },
    key,
    bytes(ethers.toUtf8Bytes(JSON.stringify(pending))),
  );
  localStorage.setItem(
    name,
    JSON.stringify({
      iv: ethers.hexlify(iv),
      ciphertext: ethers.hexlify(new Uint8Array(ciphertext)),
    }),
  );
}

export async function loadHandleRegistration(
  name: string,
  key: CryptoKey,
): Promise<PendingHandleRegistration | null> {
  const raw = localStorage.getItem(name);
  if (!raw) return null;
  try {
    const { iv, ciphertext } = JSON.parse(raw);
    const value = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: bytes(ethers.getBytes(iv)),
        additionalData: bytes(ethers.toUtf8Bytes(name)),
      },
      key,
      bytes(ethers.getBytes(ciphertext)),
    );
    return JSON.parse(new TextDecoder().decode(value));
  } catch {
    // Never discard a possibly submitted registration and silently replace it.
    throw new Error(
      "Your saved registration could not be opened. Reconnect the same wallet and try again.",
    );
  }
}

export class HandleRegistrationPending extends Error {
  constructor() {
    super(
      "Registration is still being confirmed. Click Register to resume this attempt.",
    );
  }
}

function matches(
  state: RegistryState,
  pending: PendingHandleRegistration,
): boolean {
  if (!state.exists) return false;
  const b = pending.body;
  if (state.ownerCommitment.toLowerCase() !== b.ownerCommitment.toLowerCase())
    throw new Error("This handle belongs to another account.");
  if (BigInt(state.epoch) < BigInt(b.epoch)) return false;
  if (
    BigInt(state.epoch) !== BigInt(b.epoch) ||
    state.recordCommitment.toLowerCase() !== b.recordCommitment.toLowerCase()
  )
    throw new Error(
      "The handle details changed. Reconnect the same wallet before continuing.",
    );
  return true;
}

export async function completeHandleRegistration({
  pending,
  readState,
  submit,
  publish,
  onConfirming = () => {},
  attempts = 12,
  delayMs = 2000,
}: {
  pending: PendingHandleRegistration;
  readState: () => Promise<RegistryState>;
  submit: () => Promise<void>;
  publish: () => Promise<void>;
  onConfirming?: () => void;
  attempts?: number;
  delayMs?: number;
}): Promise<void> {
  // A successful transaction with a lost response needs publication only.
  if (matches(await readState(), pending)) {
    await publish();
    return;
  }
  let submissionError: unknown;
  try {
    await submit();
  } catch (error) {
    submissionError = error;
  }
  onConfirming();
  for (let i = 0; i < attempts; i++) {
    let state: RegistryState | undefined;
    try {
      state = await readState();
    } catch {
      /* RPC outage is not a revert. */
    }
    if (state && matches(state, pending)) {
      await publish();
      return;
    }
    if (i + 1 < attempts)
      await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  // Even an HTTP error may follow a successful broadcast. Keep the exact
  // attempt; a future invocation reconciles before any relay retry.
  if (
    submissionError instanceof Error &&
    !(submissionError instanceof HandleRegistrationPending)
  )
    throw submissionError;
  throw new HandleRegistrationPending();
}
